import { jsPDF } from "jspdf";
import type { DeaneryOption, ParishOption } from "../../../utils/deanery_parishes";
import { eventOptions, type EventFormState, type RegistrationEventId } from "../data";
import { ICYM_LOGO_BASE64 } from "./icymLogoBase64";

export type MainRegistrationPdfData = {
  type: "main";
  selectedDeanery: DeaneryOption | null;
  selectedParish: ParishOption | null;
  presidentName: string;
  presidentContactNumber: string;
  presidentEmail: string;
  animatorName: string;
  animatorContactNumber: string;
  totalParticipantsCount: string;
  selectedEvents: RegistrationEventId[];
  eventForms: EventFormState;
  paymentPayerName?: string;
  paymentDate?: string;
  paymentAmount?: string;
  paymentScreenshot?: string;
};

export type PitchBattlePdfData = {
  type: "pitch_battle";
  selectedDeanery: DeaneryOption | null;
  selectedParish: ParishOption | null;
  presidentName: string;
  presidentContactNumber: string;
  presidentEmail: string;
  animatorName: string;
  animatorContactNumber: string;
  ideaTitle: string;
  entryType: "individual" | "group";
  teamLeadContact: string;
  participantCount: string;
  participantNames: string[];
};

export type DycPdfData = MainRegistrationPdfData | PitchBattlePdfData;

export function generateDycRegistrationPdf(data: DycPdfData): {
  doc: jsPDF;
  blobUrl: string;
  fileName: string;
} {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;
  const marginX = 15;
  const contentWidth = pageWidth - marginX * 2;
  let y = 15;

  const drawHeader = (isContinued = false) => {
    // Top banner box
    const headerHeight = 24;
    doc.setFillColor(27, 54, 93); // #1B365D Primary theme dark blue
    doc.rect(marginX, y, contentWidth, headerHeight, "F");

    // ICYM Logo in top-left corner of the header
    const logoSize = 18;
    const logoPadding = 3;
    try {
      doc.addImage(
        ICYM_LOGO_BASE64,
        "JPEG",
        marginX + logoPadding,
        y + (headerHeight - logoSize) / 2,
        logoSize,
        logoSize,
      );
    } catch (err) {
      // Fallback gracefully if logo fails to render
    }

    // Title text (shifted right to account for logo)
    const textCenterX = marginX + logoPadding + logoSize + (contentWidth - logoPadding - logoSize) / 2;
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("ICYM UDUPI DIOCESE", textCenterX, y + 7, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("DYC - YUVA DABAZO 2026", textCenterX, y + 13, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    const subTitleText =
      data.type === "pitch_battle"
        ? "PITCH BATTLE REGISTRATION RECEIPT"
        : "MAIN REGISTRATION RECEIPT";
    doc.text(
      isContinued ? `${subTitleText} (CONTINUED)` : subTitleText,
      textCenterX,
      y + 19,
      { align: "center" },
    );

    y += 28;
  };

  const checkAddPage = (requiredHeight: number) => {
    if (y + requiredHeight > pageHeight - 15) {
      doc.addPage();
      y = 15;
      drawHeader(true);
    }
  };

  // 1. Initial Header
  drawHeader();

  // Submission metadata bar
  doc.setFillColor(241, 245, 249);
  doc.rect(marginX, y, contentWidth, 8, "F");
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.rect(marginX, y, contentWidth, 8, "S");

  doc.setTextColor(51, 65, 85);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const nowStr = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  doc.text(`Generated On: ${nowStr}`, marginX + 3, y + 5.2);
  doc.text(
    "Registration Status: SUBMITTED",
    marginX + contentWidth - 3,
    y + 5.2,
    { align: "right" },
  );

  y += 12;

  // Helper to draw section headers
  const drawSectionTitle = (title: string) => {
    checkAddPage(15);
    doc.setFillColor(234, 242, 250);
    doc.rect(marginX, y, contentWidth, 7, "F");
    doc.setDrawColor(27, 54, 93);
    doc.setLineWidth(0.5);
    doc.line(marginX, y, marginX, y + 7);

    doc.setTextColor(27, 54, 93);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(title, marginX + 4, y + 5);

    y += 9;
  };

  // Helper to draw key-value table grid
  const drawKeyValueGrid = (items: { label: string; value: string }[]) => {
    const baseRowHeight = 7;
    const lineSpacing = 3.5;
    const colWidth = contentWidth / 2;

    for (let i = 0; i < items.length; i += 2) {
      const item1 = items[i];
      const item2 = items[i + 1];

      // Calculate the number of lines required for the values in each column
      const val1Lines = doc.splitTextToSize(item1.value || "N/A", colWidth - 35);
      const val2Lines = item2 ? doc.splitTextToSize(item2.value || "N/A", colWidth - 35) : [];
      const maxLines = Math.max(val1Lines.length, val2Lines.length, 1);
      const currentRowHeight = baseRowHeight + (maxLines - 1) * lineSpacing;

      // Check if page needs to be added before drawing the row
      checkAddPage(currentRowHeight + 2);

      // Zebra background
      if ((i / 2) % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(marginX, y, contentWidth, currentRowHeight, "F");
      }

      // Border
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.rect(marginX, y, contentWidth, currentRowHeight, "S");

      // Column 1
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`${item1.label}:`, marginX + 3, y + 4.8);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      for (let j = 0; j < val1Lines.length; j++) {
        doc.text(val1Lines[j], marginX + 35, y + 4.8 + j * lineSpacing);
      }

      // Column 2
      if (item2) {
        doc.line(marginX + colWidth, y, marginX + colWidth, y + currentRowHeight);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text(`${item2.label}:`, marginX + colWidth + 3, y + 4.8);

        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        for (let j = 0; j < val2Lines.length; j++) {
          doc.text(val2Lines[j], marginX + colWidth + 35, y + 4.8 + j * lineSpacing);
        }
      }

      y += currentRowHeight;
    }
    y += 4;
  };

  // --- SECTION 1: UNIT & PARISH DETAILS ---
  drawSectionTitle("1. Unit Details");
  const unitInfoItems = [
    { label: "Deanery", value: data.selectedDeanery?.name || "N/A" },
    { label: "Parish / Unit", value: data.selectedParish?.value || "N/A" },
    {
      label: "President Name",
      value: `${data.presidentName} (${data.presidentContactNumber})`,
    },
    {
      label: "Animator Name",
      value: `${data.animatorName} (${data.animatorContactNumber})`,
    },
    { label: "President Email", value: data.presidentEmail },
    ...(data.type === "main"
      ? [
          {
            label: "Total Unit Members",
            value: `${data.totalParticipantsCount} Participants`,
          },
        ]
      : []),
  ];
  drawKeyValueGrid(unitInfoItems);

  // --- SECTION 2: ONLINE PAYMENT DETAILS (MAIN REGISTRATION) ---
  if (data.type === "main") {
    drawSectionTitle("2. Online Payment Details");
    const formattedPaymentDate = data.paymentDate
      ? data.paymentDate.includes("-")
        ? data.paymentDate.split("-").reverse().join("/")
        : data.paymentDate
      : "N/A";

    const paymentItems = [
      { label: "Payer Name", value: data.paymentPayerName || "N/A" },
      { label: "Payment Date", value: formattedPaymentDate },
      { label: "Amount Paid", value: `Rs. ${data.paymentAmount || "0"}` },
      {
        label: "Screenshot Attached",
        value: data.paymentScreenshot ? "Yes (Uploaded)" : "No",
      },
    ];
    drawKeyValueGrid(paymentItems);

    // Payment Screenshot image preview if present
    if (data.paymentScreenshot && data.paymentScreenshot.startsWith("data:image")) {
      try {
        checkAddPage(45);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text("Payment Screenshot Preview:", marginX + 3, y + 3);
        y += 5;

        const imgFormat = data.paymentScreenshot.includes("image/png") ? "PNG" : "JPEG";
        doc.addImage(data.paymentScreenshot, imgFormat, marginX + 3, y, 60, 35);
        y += 40;
      } catch (err) {
        // Fallback gracefully if image fails to render
      }
    }
  }

  // --- SECTION 3: EVENT SPECIFIC DETAILS ---
  if (data.type === "main") {
    drawSectionTitle("3. Selected Participation Events");

    if (data.selectedEvents.length === 0) {
      checkAddPage(10);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text("No unit events selected.", marginX + 4, y + 4);
      y += 8;
    } else {
      for (const eventId of data.selectedEvents) {
        const label = eventOptions.find((e) => e.id === eventId)?.label || eventId;

        if (eventId === "rhythmroots") {
          const count = Number(data.eventForms.rhythmroots.participantCount || "0");
          const names = data.eventForms.rhythmroots.participantNames
            .slice(0, count)
            .filter(Boolean);

          checkAddPage(15);
          doc.setFillColor(248, 250, 252);
          doc.rect(marginX, y, contentWidth, 6, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(27, 54, 93);
          doc.text(`${label} (${count} Participants)`, marginX + 3, y + 4.2);
          y += 7;

          const rrItems = names.map((name, idx) => ({
            label: `Participant ${idx + 1}`,
            value: name,
          }));
          drawKeyValueGrid(rrItems);
        } else if (eventId === "spotlight") {
          const spotlight = data.eventForms.spotlight;
          const count = Number(spotlight.participantCount || "0");
          const names = spotlight.participantNames
            .slice(0, count)
            .filter(Boolean);

          checkAddPage(20);
          doc.setFillColor(248, 250, 252);
          doc.rect(marginX, y, contentWidth, 6, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(27, 54, 93);
          doc.text(`${label} (${count} Participants)`, marginX + 3, y + 4.2);
          y += 7;

          const spotlightItems = [
            { label: "Performance Slot", value: spotlight.performingEventName },
            { label: "Team Lead Contact", value: spotlight.teamLeadContact },
            { label: "Event Description", value: spotlight.eventDescription },
            { label: "Participants", value: names.join(", ") },
          ];
          drawKeyValueGrid(spotlightItems);
        } else if (eventId === "jam") {
          const jam = data.eventForms.jam;
          checkAddPage(15);
          doc.setFillColor(248, 250, 252);
          doc.rect(marginX, y, contentWidth, 6, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(27, 54, 93);
          doc.text(label, marginX + 3, y + 4.2);
          y += 7;

          drawKeyValueGrid([
            { label: "Participant Name", value: jam.participantName },
            { label: "Contact Number", value: jam.participantContact },
          ]);
        } else if (eventId === "ecomosaic") {
          const eco = data.eventForms.ecomosaic;
          checkAddPage(15);
          doc.setFillColor(248, 250, 252);
          doc.rect(marginX, y, contentWidth, 6, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(27, 54, 93);
          doc.text(label, marginX + 3, y + 4.2);
          y += 7;

          const ecoItems = [
            {
              label: "Participant 1",
              value: `${eco.primaryName} (${eco.primaryContact})`,
            },
            ...(eco.hasSecondParticipant
              ? [
                  {
                    label: "Participant 2",
                    value: `${eco.secondaryName} (${eco.secondaryContact})`,
                  },
                ]
              : []),
          ];
          drawKeyValueGrid(ecoItems);
        } else if (eventId === "faithvault") {
          const fv = data.eventForms.faithvault;
          checkAddPage(15);
          doc.setFillColor(248, 250, 252);
          doc.rect(marginX, y, contentWidth, 6, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(27, 54, 93);
          doc.text(label, marginX + 3, y + 4.2);
          y += 7;

          drawKeyValueGrid([
            { label: "Team Lead Contact", value: fv.teamLeadContact },
            {
              label: "Team Members",
              value: fv.memberNames.filter(Boolean).join(", "),
            },
          ]);
        } else if (eventId === "pitchbattle") {
          checkAddPage(12);
          doc.setFillColor(248, 250, 252);
          doc.rect(marginX, y, contentWidth, 6, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(27, 54, 93);
          doc.text(`${label} (Confirmed)`, marginX + 3, y + 4.2);
          y += 10;
        }
      }
    }
  } else {
    // Pitch Battle specific section
    drawSectionTitle("2. Pitch Battle Event Details");

    const count = Number(data.participantCount || "1");
    const activeNames = data.participantNames.slice(0, count).filter(Boolean);

    const pitchItems = [
      { label: "Pitch Title", value: data.ideaTitle },
      {
        label: "Entry Type",
        value: data.entryType === "individual" ? "Individual Entry" : "Group Entry",
      },
      { label: "Team Lead Contact", value: data.teamLeadContact },
      { label: "Participant Count", value: `${data.participantCount}` },
      { label: "Participant Names", value: activeNames.join(", ") },
    ];
    drawKeyValueGrid(pitchItems);
  }

  // --- FOOTER & PAGE NUMBERS ---
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, pageHeight - 12, marginX + contentWidth, pageHeight - 12);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      "ICYM Udupi Diocese - DYC Yuva Dabazo 2026 Official Registration Record",
      marginX,
      pageHeight - 7,
    );
    doc.text(
      `Page ${i} of ${totalPages}`,
      marginX + contentWidth,
      pageHeight - 7,
      { align: "right" },
    );
  }

  const parishClean = (data.selectedParish?.value || "Unit").replace(/[^a-zA-Z0-9]/g, "_");
  const prefix = data.type === "pitch_battle" ? "Pitch_Battle_Registration" : "Yuva_Dabazo_2026_Registration";
  const fileName = `${prefix}_${parishClean}.pdf`;

  const blob = doc.output("blob");
  const blobUrl = URL.createObjectURL(blob);

  return { doc, blobUrl, fileName };
}
